// Lazy `sonner` (≈ 10 KB gz kept out of main.js). craco.config.js redirects
// every bare `sonner` import in the web build to this file, except the one
// below, so `import { toast } from 'sonner'` gets this facade and the real
// library loads on the first toast, or when the browser is idle after the
// page has loaded. Calls made before it arrives are queued with an id
// (returned, so they can still be dismissed) and shown as soon as the
// <Toaster> below has mounted.
// Jest does not redirect `sonner`: other tests use (or mock) the real package.

import React, { useEffect, useState } from 'react';

let real = null; // the sonner module once loaded
let mounted = false; // a real <Toaster> is subscribed
let loading = null;
let seq = 0;
const pending = [];
const requestListeners = new Set();

const loadSonner = () => {
  if (!loading) {
    loading = import('sonner').then((mod) => {
      real = mod;
      return mod;
    });
    loading.catch(() => { loading = null; }); // offline: try again on the next toast
  }
  return loading;
};

const request = () => requestListeners.forEach((listener) => listener());

const flush = () => {
  while (pending.length) {
    const [method, args] = pending.shift();
    if (method) real.toast[method](...args);
    else real.toast(...args);
  }
};

// Admin pages: every toast can be closed, and errors/warnings stay 8 s.
const onAdmin = () => typeof window !== 'undefined' && /^\/admin(\/|$)/.test(window.location.pathname);
export const withToastDefaults = (method, options = {}) => (onAdmin()
  ? { closeButton: true, ...(method === 'error' || method === 'warning' ? { duration: 8000 } : {}), ...options }
  : options);

const call = (method) => (message, opts = {}) => {
  const options = withToastDefaults(method, opts);
  if (real && mounted) return method ? real.toast[method](message, options) : real.toast(message, options);
  const id = options.id ?? `anum-toast-${seq += 1}`;
  pending.push([method, [message, { ...options, id }]]);
  request();
  return id;
};

export const toast = Object.assign(call(null), {
  success: call('success'),
  error: call('error'),
  info: call('info'),
  warning: call('warning'),
  message: call('message'),
  loading: call('loading'),
  dismiss: (id) => {
    if (real && mounted) return real.toast.dismiss(id);
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      if (id === undefined || pending[i][1][1].id === id) pending.splice(i, 1);
    }
    return id;
  },
  // Rare helpers: run once the library is there.
  promise: (...args) => { request(); return loadSonner().then((mod) => mod.toast.promise(...args)); },
  custom: (...args) => { request(); return loadSonner().then((mod) => mod.toast.custom(...args)); },
});

/** Warm-up a few seconds after `load`, when idle, so a later first toast
 *  appears at once without competing with the page's own downloads. */
const WARM_DELAY_MS = 3000;
const warmWhenIdle = () => {
  if (typeof window === 'undefined') return () => {};
  let idleId = null;
  let timer = null;
  const start = () => {
    timer = setTimeout(() => {
      timer = null;
      if (window.requestIdleCallback) idleId = window.requestIdleCallback(request, { timeout: 8000 });
      else request();
    }, WARM_DELAY_MS);
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
  return () => {
    window.removeEventListener('load', start);
    if (idleId !== null && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
    if (timer !== null) clearTimeout(timer);
  };
};

/** Drop-in for sonner's <Toaster>: renders it once the library has loaded. */
export const Toaster = (props) => {
  const [mod, setMod] = useState(real);

  useEffect(() => {
    if (mod) return undefined;
    let alive = true;
    const onRequest = () => {
      loadSonner().then((loaded) => { if (alive) setMod(loaded); }, () => {});
    };
    requestListeners.add(onRequest);
    if (pending.length) onRequest();
    const stopWarm = warmWhenIdle();
    return () => {
      alive = false;
      requestListeners.delete(onRequest);
      stopWarm();
    };
  }, [mod]);

  // Child effects run first, so the real Toaster has subscribed by now.
  useEffect(() => {
    if (!mod) return undefined;
    mounted = true;
    flush();
    return () => { mounted = false; };
  }, [mod]);

  return mod && mod.Toaster ? React.createElement(mod.Toaster, props) : null;
};

export default toast;
